import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fmtEur } from "../../lib/format";
import { CheckCircle2, AlertTriangle, FileSpreadsheet, FileText, Wallet, Loader2, Info } from "lucide-react";
import { DocumentPreviewDialog } from "../DocumentPreviewDialog";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
export const ISSUE_LABELS = {
    no_file: { label: "Manca nel file consulente", cls: "bg-slate-100 text-slate-700 ring-slate-300", info: true },
    solo_file: { label: "Solo nel file", cls: "bg-amber-50 text-amber-900 ring-amber-200" },
    no_cedolino: { label: "Ricevuta senza cedolino", cls: "bg-amber-50 text-amber-900 ring-amber-200" },
    no_ricevuta: { label: "Cedolino senza ricevuta", cls: "bg-red-50 text-red-800 ring-red-200" },
    file_vs_cedolino: { label: "File ≠ cedolino (lordo)", cls: "bg-red-50 text-red-800 ring-red-200" },
    cedolino_vs_ricevuta: { label: "Cedolino ≠ ricevuta (netto)", cls: "bg-red-50 text-red-800 ring-red-200" },
    file_vs_ricevuta: { label: "File ≠ ricevuta (stima)", cls: "bg-red-50 text-red-800 ring-red-200" },
};

const cellTone = (issues) => {
    if (!issues.length) return "bg-emerald-50 text-emerald-900 ring-emerald-200";
    if (issues.every((i) => ISSUE_LABELS[i]?.info)) return "bg-sky-50 text-sky-900 ring-sky-200";
    return "bg-red-50 text-red-900 ring-red-200";
};

const Cell = ({ m, onOpenDoc }) => {
    if (m.status === "none") return <td className="border-l border-slate-100 px-1 py-1 text-center text-slate-200">·</td>;
    const title = m.issues.length ? m.issues.map((i) => ISSUE_LABELS[i]?.label || i).join(" · ") : "File, cedolino e ricevuta coincidono";
    return (
        <td className="border-l border-slate-100 px-1 py-1 align-top" data-testid={`recon-cell-${m.month}`}>
            <div className={`space-y-0.5 rounded px-1.5 py-1 text-[11px] leading-tight ring-1 ${cellTone(m.issues)}`} title={title}>
                <div className="flex items-center gap-1" title="File consulente (lordo)"><FileSpreadsheet className="h-3 w-3 shrink-0 opacity-60" /><span className="font-mono-numbers">{m.file === null ? "—" : fmtEur(m.file)}</span></div>
                <div className="flex items-center gap-1" title="Cedolino TFR (lordo → netto)">
                    <FileText className="h-3 w-3 shrink-0 opacity-60" />
                    {m.cedolino ? (
                        <span className="font-mono-numbers">
                            {m.cedolino.items.map((s, i) => (
                                <button key={s.id} type="button" onClick={() => onOpenDoc(s)} className="underline decoration-dotted hover:text-[#376DB3]" title={`${s.filename || "cedolino"} · aliquota ${s.aliquota ?? "—"}%`} data-testid={`recon-cedolino-${s.id}`}>
                                    {i > 0 && " + "}{fmtEur(s.lordo)}
                                </button>
                            ))}
                            <span className="opacity-60"> → {fmtEur(m.cedolino.netto)}</span>
                        </span>
                    ) : <span className="font-mono-numbers">—</span>}
                </div>
                <div className="flex items-center gap-1" title="Ricevuta del portale (netto erogato)">
                    <Wallet className="h-3 w-3 shrink-0 opacity-60" />
                    {m.portal === null ? <span className="font-mono-numbers">—</span> : (
                        <span className="font-mono-numbers">
                            {m.portal_items.map((it, i) => (
                                <Link key={it.id} to={`/acconti/${it.id}`} className="underline decoration-dotted hover:text-[#376DB3]" title={`Ricevuta ${it.receipt || ""} · ${it.recovery_payslip || ""}`}>
                                    {i > 0 && " + "}{fmtEur(it.amount)}
                                </Link>
                            ))}
                            {!m.cedolino && <span className="ml-1 opacity-60">(≈ {fmtEur(m.portal_lordo_est)} lordo stimato)</span>}
                        </span>
                    )}
                </div>
            </div>
        </td>
    );
};

export const TfrQuadraturaTable = ({ data, loading }) => {
    const [onlyDiff, setOnlyDiff] = useState(true);
    const [previewDoc, setPreviewDoc] = useState(null);
    const rows = useMemo(() => (data?.rows || []).filter((r) => !onlyDiff || r.severity !== "ok"), [data, onlyDiff]);
    if (loading || !data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
    if (!data.has_file && !data.has_payslips && !data.rows.length) {
        return (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="recon-no-file">
                Per il {data.year} non ci sono anticipi del file consulente né cedolini TFR da confrontare.
            </div>
        );
    }
    const sc = data.severity_counts;
    const ic = data.issue_counts || {};
    return (
        <div data-testid="tfr-quadratura">
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-800 ring-1 ring-emerald-200" data-testid="recon-count-ok"><CheckCircle2 className="h-3.5 w-3.5" /> {sc.ok} in quadratura</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 font-semibold text-red-800 ring-1 ring-red-200" data-testid="recon-count-diff"><AlertTriangle className="h-3.5 w-3.5" /> {sc.warn} da verificare</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 font-semibold text-sky-900 ring-1 ring-sky-200" data-testid="recon-count-info"><Info className="h-3.5 w-3.5" /> {sc.info} solo file non aggiornato</span>
                <span className="mx-1 h-4 w-px bg-slate-200" />
                {Object.entries(ic).map(([k, n]) => (
                    <span key={k} className={`rounded px-2 py-0.5 text-[11px] font-medium ring-1 ${ISSUE_LABELS[k]?.cls || ""}`} data-testid={`recon-issue-${k}`}>{ISSUE_LABELS[k]?.label || k}: {n}</span>
                ))}
                <label className="ml-auto inline-flex items-center gap-2 text-slate-600">
                    <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} data-testid="recon-only-diff" /> Solo da verificare
                </label>
            </div>
            <p className="px-4 pt-3 text-[11px] text-slate-500">
                Per ogni mese di busta paga: <FileSpreadsheet className="mx-0.5 inline h-3 w-3" /> <b>lordo</b> del file consulente ·
                <FileText className="mx-0.5 inline h-3 w-3" /> <b>cedolino TFR</b> letto dall'archivio (lordo → netto bonificato) ·
                <Wallet className="mx-0.5 inline h-3 w-3" /> <b>ricevuta</b> del portale (netto erogato).
                Abbinamento cedolino↔ricevuta per mese e netto (±1 €); senza cedolino il lordo è stimato al {data.aliquota}% (tolleranza ±{data.tolerance_pct}%).
                Totali {data.year}: file {fmtEur(data.totals.file)} · cedolini {fmtEur(data.totals.cedolino)} · ricevute {fmtEur(data.totals.portal)} (≈ {fmtEur(data.totals.portal_lordo)} lordo).
            </p>
            <div className="overflow-x-auto p-4">
                <table className="w-full min-w-[1200px] text-xs" data-testid="recon-table">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                            <th className="px-2 py-1">Matr.</th>
                            <th className="px-2 py-1">Dipendente</th>
                            {MONTHS.map((m) => <th key={m} className="border-l border-slate-100 px-1 py-1 text-center">{m}</th>)}
                            <th className="border-l border-slate-200 px-2 py-1 text-right">Totali</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={15} className="py-10 text-center text-slate-500">{onlyDiff ? "Nessuna differenza: file, cedolini e ricevute coincidono." : "Nessun dato."}</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.employee_id} className="border-t border-slate-100 align-top" data-testid={`recon-row-${r.employee_id}`}>
                                <td className="px-2 py-2 font-mono-numbers text-slate-500">{r.matricola || "—"}</td>
                                <td className="px-2 py-2 font-medium text-slate-800">
                                    {r.cognome} {r.nome}
                                    {r.severity === "ok" && <CheckCircle2 className="ml-1 inline h-3.5 w-3.5 text-emerald-600" />}
                                    {r.severity === "info" && <Info className="ml-1 inline h-3.5 w-3.5 text-sky-600" />}
                                    {r.severity === "warn" && <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-red-600" />}
                                </td>
                                {r.months.map((m) => <Cell key={m.month} m={m} onOpenDoc={(s) => setPreviewDoc({ id: s.document_id, filename: s.filename })} />)}
                                <td className="border-l border-slate-200 px-2 py-2 text-right font-mono-numbers">
                                    <div title="File">{fmtEur(r.file_total)}</div>
                                    <div className="text-slate-600" title="Cedolini (lordo)">{fmtEur(r.cedolino_total)}</div>
                                    <div className="text-slate-500" title="Ricevute (netto)">{fmtEur(r.portal_total)}</div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <DocumentPreviewDialog open={!!previewDoc} document={previewDoc} onClose={() => setPreviewDoc(null)} />
        </div>
    );
};
